import { useMemo } from 'react';
import { where } from 'firebase/firestore';
import { useCollection } from 'core/hooks/useCollection';
import { TransportJobsApiService } from 'core/api';
import { ITransportJobDao } from 'core/api/types/transport-job.interface';
import TransportGroupsApiService from 'core/api/transport-groups/transport-groups-api.service';
import { ITransportGroup } from 'core/api/transport-groups/transport-groups-api-interface';

/**
 * What is waiting on each transport segment.
 *
 * Deliberately not "how many rows are in the table" — that is on screen already,
 * and a number repeating what you can see is decoration. These count work that
 * nobody has yet: jobs still in the pool, and batches no driver has accepted.
 * Those are the numbers worth reading before deciding which segment to open.
 *
 * Asked of the database rather than counted from the loaded page, for the same
 * reason as the review badge: a screen loads a page at a time, so a count taken
 * from it stops at a page and understates itself exactly when the backlog is
 * worth knowing about.
 */

/** Enough to be useful without subscribing to an unbounded set. */
const LIMIT = 99;

const asLabel = (count: number) => (count >= LIMIT ? `${LIMIT}+` : String(count));

export const useTransportCounts = () => {
  const pooled = useMemo(() => [where('status', '==', 'pending')], []);
  const open = useMemo(() => [where('status', 'in', ['posted', 'assigned'])], []);

  const { data: jobs } = useCollection<ITransportJobDao>({
    apiService: TransportJobsApiService,
    constraints: pooled,
    initialLimit: LIMIT,
  });

  const { data: batches } = useCollection<ITransportGroup>({
    apiService: TransportGroupsApiService,
    constraints: open,
    initialLimit: LIMIT,
  });

  return {
    jobs: { count: jobs?.length ?? 0, label: asLabel(jobs?.length ?? 0) },
    batches: { count: batches?.length ?? 0, label: asLabel(batches?.length ?? 0) },
  };
};
